import Slider from '@react-native-community/slider'
import { useCallback, useEffect, useRef, useState } from 'react'
import { StyleSheet, View } from 'react-native'
import { Button, Text, TextInput } from 'react-native-paper'
import { waitForGoogleMapsReady } from '@/hooks/useIsGoogleMapsReady'
import { geocodeAddressString } from '@/lib/geocoding'
import { DEFAULT_CIRCLE_RADIUS } from '@/lib/locations'
import { type GeoJSONPoint, MIN_GEOFENCE_RADIUS } from '@/types/locations'
import MapView, {
    Circle,
    type MapPressEvent,
    Marker,
    type MarkerDragStartEndEvent,
    type Region,
} from '@/ui/MapView'

interface PointMapProps {
    value: GeoJSONPoint
    onChange: (value: GeoJSONPoint | undefined) => void
}

const calculateRegionForPoint = async (c: {
    latitude: number
    longitude: number
    radius: number
}): Promise<Region> => {
    await waitForGoogleMapsReady()

    if (c.latitude === 0 && c.longitude === 0) {
        return {
            latitude: 38,
            longitude: -92,
            latitudeDelta: 17,
            longitudeDelta: 17,
        }
    }
    const radiusInDegrees = c.radius / 111320

    const latDelta = Math.max(radiusInDegrees * 1.5, 0.002)
    const lngDelta = Math.max(radiusInDegrees * 1.5, 0.002)

    return {
        latitude: c.latitude,
        longitude: c.longitude,
        latitudeDelta: latDelta,
        longitudeDelta: lngDelta,
    }
}

const propsToState = (value: GeoJSONPoint) => ({
    longitude: value.coordinates[0],
    latitude: value.coordinates[1],
    radius: Math.max(value.radius || DEFAULT_CIRCLE_RADIUS, MIN_GEOFENCE_RADIUS),
})

export function PointMap({ value, onChange }: PointMapProps) {
    const mapRef = useRef<MapView>(null)
    const [addressSearch, setAddressSearch] = useState({ query: '', loading: false, error: '' })

    const [coord, setCoordinate] = useState<{
        latitude: number
        longitude: number
        radius: number
    }>(propsToState(value))

    // Tracks the lat/lng most recently emitted by this component so we can detect
    // when the value prop changes for an external reason (e.g. parent finished
    // loading the record) vs. a local interaction (drag, tap, geocode).
    const lastEmittedRef = useRef<{ latitude: number; longitude: number }>({
        latitude: coord.latitude,
        longitude: coord.longitude,
    })

    const animateToCoord = useCallback(
        async (target: { latitude: number; longitude: number; radius: number }) => {
            if (!mapRef.current) return
            const region = await calculateRegionForPoint(target)
            mapRef.current?.animateToRegion(region, 300)
        },
        []
    )

    // Sync coord state from incoming value, and zoom only when the value prop
    // brings a new lat/lng — skip when the prop update is an echo of what we
    // just emitted locally (drag, tap, geocode) or a radius-only change.
    useEffect(() => {
        const next = propsToState(value)
        setCoordinate(next)
        if (next.latitude === 0 && next.longitude === 0) return
        const isSameCoord =
            next.latitude === lastEmittedRef.current.latitude &&
            next.longitude === lastEmittedRef.current.longitude
        lastEmittedRef.current = { latitude: next.latitude, longitude: next.longitude }
        if (isSameCoord) return
        const timeout = setTimeout(() => animateToCoord(next), 100)
        return () => clearTimeout(timeout)
    }, [value, animateToCoord])

    const handleMapPress = (event: MapPressEvent) => {
        const newCoordinate = event.nativeEvent.coordinate
        handleCoordinateChange(newCoordinate)
    }

    const handleCoordinateChange = (newCoordinate: { latitude: number; longitude: number }) => {
        setCoordinate({ ...newCoordinate, radius: coord.radius })
        lastEmittedRef.current = newCoordinate
        onChange({
            type: 'Point',
            coordinates: [newCoordinate.longitude, newCoordinate.latitude],
            radius: coord.radius,
        })
    }

    const handleRadiusChange = (newRadius: number) => {
        setCoordinate({ ...coord, radius: newRadius })
        onChange({
            type: 'Point',
            coordinates: [coord.longitude, coord.latitude],
            radius: newRadius,
        })
    }

    const handleMarkerDragEnd = (event: MarkerDragStartEndEvent) => {
        const newCoordinate = event.nativeEvent.coordinate
        handleCoordinateChange(newCoordinate)
    }

    const handleGeocodeAddress = async () => {
        if (!addressSearch.query.trim()) return

        setAddressSearch(prev => ({ ...prev, loading: true, error: '' }))
        const result = await geocodeAddressString(addressSearch.query)

        if (result) {
            handleCoordinateChange({ latitude: result.latitude, longitude: result.longitude })
            animateToCoord({
                latitude: result.latitude,
                longitude: result.longitude,
                radius: coord.radius,
            })
            setAddressSearch(prev => ({ ...prev, loading: false }))
        } else {
            setAddressSearch(prev => ({
                ...prev,
                loading: false,
                error: 'Could not find that address',
            }))
        }
    }

    return (
        <View style={styles.container}>
            <View style={styles.addressBar}>
                <TextInput
                    mode="outlined"
                    dense
                    placeholder="Paste an address to locate on map"
                    value={addressSearch.query}
                    onChangeText={text =>
                        setAddressSearch({ query: text, loading: false, error: '' })
                    }
                    onSubmitEditing={handleGeocodeAddress}
                    style={styles.addressInput}
                    error={!!addressSearch.error}
                    disabled={addressSearch.loading}
                />
                <Button
                    mode="contained"
                    onPress={handleGeocodeAddress}
                    loading={addressSearch.loading}
                    disabled={addressSearch.loading || !addressSearch.query.trim()}
                    compact
                    style={styles.addressButton}
                >
                    Locate
                </Button>
            </View>
            {addressSearch.error ? (
                <Text variant="bodySmall" style={styles.addressError}>
                    {addressSearch.error}
                </Text>
            ) : null}
            <MapView ref={mapRef} style={styles.map} onPress={handleMapPress} zoomControlEnabled>
                {coord.latitude !== 0 && (
                    <>
                        <Circle
                            center={coord}
                            radius={coord.radius}
                            strokeColor="#007AFF"
                            strokeWidth={2}
                            fillColor="rgba(0, 122, 255, 0.1)"
                        />
                        <Marker
                            coordinate={coord}
                            draggable
                            onDragEnd={handleMarkerDragEnd}
                            title="Circle Center"
                            description={`Lat: ${coord.latitude.toFixed(6)}, Lng: ${coord.longitude.toFixed(6)}`}
                        />
                    </>
                )}
            </MapView>

            <View style={styles.actions}>
                <Text variant="bodySmall" style={styles.hint}>
                    {coord.latitude
                        ? 'Drag the marker to move the circle center'
                        : 'Tap on the map to place the circle center'}
                </Text>

                {coord.latitude && (
                    <View style={styles.sliderContainer}>
                        <Text variant="labelMedium" style={styles.sliderLabel}>
                            Radius: {coord.radius.toFixed(0)} meters
                        </Text>
                        <Slider
                            style={styles.slider}
                            minimumValue={MIN_GEOFENCE_RADIUS}
                            maximumValue={2000}
                            value={coord.radius}
                            onValueChange={handleRadiusChange}
                            step={5}
                            minimumTrackTintColor="#007AFF"
                            maximumTrackTintColor="#d1d5db"
                        />
                        <View style={styles.sliderLabels}>
                            <Text variant="bodySmall" style={styles.sliderMinMax}>
                                {MIN_GEOFENCE_RADIUS}m
                            </Text>
                            <Text variant="bodySmall" style={styles.sliderMinMax}>
                                2000m
                            </Text>
                        </View>
                    </View>
                )}
            </View>
        </View>
    )
}

const styles = StyleSheet.create({
    container: {
        borderWidth: 1,
        borderColor: '#d1d5db',
        borderRadius: 8,
        overflow: 'hidden',
        minHeight: 400,
    },
    addressBar: {
        flexDirection: 'row',
        alignItems: 'center',
        padding: 8,
        gap: 8,
        backgroundColor: '#f9fafb',
    },
    addressInput: {
        flex: 1,
    },
    addressButton: {
        alignSelf: 'center',
    },
    addressError: {
        color: '#dc2626',
        paddingHorizontal: 12,
        paddingBottom: 4,
        backgroundColor: '#f9fafb',
    },
    map: {
        flex: 1,
        minHeight: 350,
    },
    actions: {
        padding: 12,
        backgroundColor: '#f9fafb',
    },
    hint: {
        color: '#6b7280',
        fontStyle: 'italic',
        marginBottom: 8,
    },
    sliderContainer: {
        gap: 8,
    },
    sliderLabel: {
        fontWeight: '600',
        color: '#374151',
        textAlign: 'center',
    },
    slider: {
        width: '100%',
        height: 40,
    },
    sliderThumb: {
        backgroundColor: '#007AFF',
        width: 20,
        height: 20,
    },
    sliderLabels: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        paddingHorizontal: 8,
    },
    sliderMinMax: {
        color: '#6b7280',
        fontSize: 12,
    },
})

export default PointMap
